import React, { useState, useEffect } from 'react';
import '../../styles/AutomationCard.css';
import { Button, IconButton, Input, Dropdown, ExitScreen } from '../';
import { Exit } from '../../assets/icons';
import { HugeiconsIcon } from '@hugeicons/react';
import { Edit02Icon, AiMail02Icon } from '@hugeicons/core-free-icons';
import { colour } from '../../styles/GlobalStyles';
import { getOutputSummary } from './utils';
import { setAutomation, getDocument } from '../../functions';

const INFO_WEBHOOK = 'Send underwriting results to a webhook.';

const getNormalizedRecipients = (recipients) => {
    return (recipients || [])
        .map(recipient => ({
            userId: recipient.userId,
            emailEnabled: recipient.emailEnabled === true,
            smsEnabled: recipient.smsEnabled === true
        }))
        .sort((a, b) => (a.userId || '').localeCompare(b.userId || ''));
};

const getNormalizedCurrentOutput = (automation) => {
    const webhookUrl = (automation?.output?.webhookUrl || '').trim();
    const recipients = automation?.output?.notifications?.recipients || [];
    return {
        webhookUrl,
        recipients: getNormalizedRecipients(recipients)
    };
};

const getNormalizedFormOutput = (formWebhookUrl, companyUsers, formEmailUserIds) => {
    const webhookUrl = (formWebhookUrl || '').trim();
    const recipients = (companyUsers || []).map(user => ({
        userId: user.userId,
        emailEnabled: formEmailUserIds.includes(user.userId),
        smsEnabled: false
    }));
    return {
        webhookUrl,
        recipients: getNormalizedRecipients(recipients)
    };
};

function OutputSection(props) {
    const { automation, onUpdate, companyId, automationId, isMLS } = props;
    
    const [outputEditOpen, setOutputEditOpen] = useState(false);
    const [outputSaving, setOutputSaving] = useState(false);
    const [hasChanges, setHasChanges] = useState(false);
    const [formWebhookUrl, setFormWebhookUrl] = useState('');
    const [formEmailUserIds, setFormEmailUserIds] = useState([]);
    // const [formSmsUserIds, setFormSmsUserIds] = useState([]);
    const [emailUsersDropdownOpen, setEmailUsersDropdownOpen] = useState(false);
    // const [smsUsersDropdownOpen, setSmsUsersDropdownOpen] = useState(false);
    const [companyUsers, setCompanyUsers] = useState([]);

    // Initialize form when opening edit
    useEffect(() => {
        if (outputEditOpen && automation) {
            setFormWebhookUrl(automation.output?.webhookUrl || '');
            const recipients = automation.output?.notifications?.recipients || [];
            setFormEmailUserIds(recipients.filter(r => r.emailEnabled === true).map(r => r.userId));
            setHasChanges(false);
            // setFormSmsUserIds(recipients.filter(r => r.smsEnabled === true).map(r => r.userId));
        }
    }, [outputEditOpen, automation]);

    // Check for changes
    useEffect(() => {
        if (!outputEditOpen || !automation) return;
        const current = getNormalizedCurrentOutput(automation);
        const form = getNormalizedFormOutput(formWebhookUrl, companyUsers, formEmailUserIds);
        const isMissingValue = (value) => (
            value === undefined ||
            value === null ||
            (typeof value === 'string' && value.trim() === '') ||
            (Array.isArray(value) && value.length === 0) ||
            (typeof value === 'object' && !Array.isArray(value) && value && Object.keys(value).length === 0)
        );

        const storedWebhookUrl = automation?.output?.webhookUrl;
        const storedRecipients = automation?.output?.notifications?.recipients;
        const hasUnsavedDefaults = isMissingValue(storedWebhookUrl) || isMissingValue(storedRecipients);

        setHasChanges(JSON.stringify(current) !== JSON.stringify(form) || hasUnsavedDefaults);
    }, [outputEditOpen, automation, formWebhookUrl, formEmailUserIds, companyUsers]);

    // Fetch company users when Output edit panel opens
    useEffect(() => {
        if (!outputEditOpen || !companyId) return;
        const fetchCompanyUsers = async () => {
            try {
                const companyDoc = await getDocument('companies', companyId);
                if (companyDoc.status === 200 && companyDoc.data?.data?.users) {
                    setCompanyUsers(companyDoc.data.data.users || []);
                } else {
                    setCompanyUsers([]);
                }
            } catch (error) {
                console.error('Error fetching company users:', error);
                setCompanyUsers([]);
            }
        };
        fetchCompanyUsers();
    }, [outputEditOpen, companyId]);

    const handleCancelOutput = () => {
        // Reset output form to current automation values
        if (automation) {
            setFormWebhookUrl(automation.output?.webhookUrl || '');
            const recipients = automation.output?.notifications?.recipients || [];
            setFormEmailUserIds(recipients.filter(r => r.emailEnabled === true).map(r => r.userId));
            // setFormSmsUserIds(recipients.filter(r => r.smsEnabled === true).map(r => r.userId));
        }
        setOutputEditOpen(false);
        setEmailUsersDropdownOpen(false);
        // setSmsUsersDropdownOpen(false);
    };

    const handleSaveOutput = async () => {
        if (!companyId || !automationId) return;
        if (!hasChanges) return;
        setOutputSaving(true);
        try {
            // Build recipients array from form state
            const existingRecipients = automation.output?.notifications?.recipients || [];
            const updatedRecipients = [...existingRecipients];
            
            // Update existing recipients
            updatedRecipients.forEach(recipient => {
                recipient.emailEnabled = formEmailUserIds.includes(recipient.userId);
                recipient.smsEnabled = false; // Hard coded to false
            });
            
            // Add any new users from companyUsers that aren't in recipients
            companyUsers.forEach(user => {
                if (!updatedRecipients.find(r => r.userId === user.userId)) {
                    updatedRecipients.push({
                        userId: user.userId,
                        email: user.email || '',
                        firstName: user.firstName || '',
                        emailEnabled: formEmailUserIds.includes(user.userId),
                        smsEnabled: false // Hard coded to false
                    });
                }
            });

            const payload = {
                output: {
                    webhookUrl: formWebhookUrl.trim() || null,
                    notifications: {
                        enabled: true, // Notifications are enabled if users are selected
                        recipients: updatedRecipients
                    }
                }
            };
            const result = await setAutomation(companyId, automationId, payload);
            if (result.status !== 200) throw new Error(result.message || 'Failed to save');
            if (onUpdate) onUpdate({ 
                ...automation, 
                output: payload.output
            });
            setOutputEditOpen(false);
        } catch (error) {
            console.error('Error saving output:', error);
            alert('Failed to save output. Please try again.');
        } finally {
            setOutputSaving(false);
        }
    };

    const outputSummary = getOutputSummary(automation, isMLS);

    return (
        <div className={`automation-form-section-card automation-detail-section-card ${outputEditOpen ? '' : 'automation-form-section-card-compact'}`}>
            <div className="automation-form-section-card-content">
                <div className="automation-form-section-card-text">
                    <span className="body-bold colour-primary">Output</span>
                    {outputSummary.notifText === 'Not configured' && (
                        <span className="body-regular colour-secondary">Decide where deals get sent.</span>
                    )}
                </div>
                <div className="automation-form-section-card-icon-button">
                    <IconButton
                        size="medium"
                        icon={outputEditOpen ? <Exit fill={colour.red03} size={14} clickFunc={null} /> : <HugeiconsIcon icon={Edit02Icon} color={colour.gray06} size={16} />}
                        disabled={false}
                        clickFunc={() => outputEditOpen ? handleCancelOutput() : setOutputEditOpen(true)}
                        standardIcon={false}
                        background={true}
                        bgType={outputEditOpen ? "destructive" : "white"}
                    />
                </div>
            </div>
            
            {outputEditOpen ? (
                <div className="automation-output-edit-panel">
                    <div className="automation-output-edit-form">
                                <div className="automation-output-webhook-section">
                                    <Input 
                                        label="Webhook" 
                                        labelType="default" 
                                        info={true} 
                                        infoText={INFO_WEBHOOK}
                                        value={formWebhookUrl} 
                                        onChange={(e) => setFormWebhookUrl(e.target.value)} 
                                        placeholder="Enter url" 
                                        type="text" 
                                        leftUnit={null} 
                                        rightUnit={null} 
                                        unitSwitch={false} 
                                        leftUnitSelected={true} 
                                        unitSwitchFunc={null} 
                                        disabled={false} 
                                        multiline={false} 
                                        error={false} 
                                    />
                                </div>
                                <div className="automation-output-crm-section">
                                    <div className="automation-output-crm-header">
                                        <span className="body-bold colour-primary">CRM</span>
                                        <span className="body-regular colour-secondary">Contact us to configure at <a className="body-regulr colour-primary" href="mailto:info@clozers.co">info@clozers.co</a></span>
                                    </div>
                                </div>
                                <div className="automation-form-divider"></div>
                                <div className="automation-output-notifications-section">
                                    <div className="automation-output-notifications-header">
                                        <span className="body-bold colour-primary">Notifications</span>
                                        <span className="body-regular colour-primary">Choose notification type and users to receive alerts.</span>
                                    </div>
                                    <div className="automation-output-notification-row">
                                        <div className="automation-output-notification-label">
                                            <HugeiconsIcon icon={AiMail02Icon} size={16} color={colour.gray06} />
                                            <span className="body-bold colour-primary">Email</span>
                                        </div>
                                        {emailUsersDropdownOpen && <ExitScreen clickScreen={() => setEmailUsersDropdownOpen(false)} />}
                                        <div className="automation-output-notification-dropdown">
                                            <Dropdown
                                                open={emailUsersDropdownOpen}
                                                icon={null}
                                                label={formEmailUserIds.length > 0 ? `${formEmailUserIds.length} user${formEmailUserIds.length !== 1 ? 's' : ''} selected` : 'Select users'}
                                                disabled={false}
                                                options={companyUsers.map(user => ({ 
                                                    label: `${user.firstName || ''} ${user.lastName || ''}`.trim() || 'Unknown',
                                                    selected: formEmailUserIds.includes(user.userId)
                                                }))}
                                                multiselect={true}
                                                toggleDropdown={() => setEmailUsersDropdownOpen(!emailUsersDropdownOpen)}
                                                cta={(item, index) => {
                                                    const userId = companyUsers[index]?.userId;
                                                    if (userId) {
                                                        const newSelected = formEmailUserIds.includes(userId)
                                                            ? formEmailUserIds.filter(id => id !== userId)
                                                            : [...formEmailUserIds, userId];
                                                        setFormEmailUserIds(newSelected);
                                                    }
                                                }}
                                                sidealign="left"
                                                topalign="bottom"
                                            />
                                        </div>
                                    </div>
                                    {/* <div className="automation-output-notification-row">
                                        <div className="automation-output-notification-label">
                                            <HugeiconsIcon icon={Message01Icon} size={16} color={colour.gray06} />
                                            <span className="body-bold colour-primary">SMS</span>
                                        </div>
                                        {smsUsersDropdownOpen && <ExitScreen clickScreen={() => setSmsUsersDropdownOpen(false)} />}
                                        <Dropdown
                                            open={smsUsersDropdownOpen}
                                            icon={null}
                                            label={formSmsUserIds.length > 0 ? `${formSmsUserIds.length} user${formSmsUserIds.length !== 1 ? 's' : ''} selected` : 'Select users'}
                                            disabled={false}
                                            options={companyUsers.map(user => ({ 
                                                label: `${user.firstName || ''} ${user.lastName || ''}`,
                                                selected: formSmsUserIds.includes(user.userId)
                                            }))}
                                            multiselect={true}
                                            toggleDropdown={() => setSmsUsersDropdownOpen(!smsUsersDropdownOpen)}
                                            cta={(item, index) => {
                                                const userId = companyUsers[index]?.userId;
                                                if (userId) {
                                                    const newSelected = formSmsUserIds.includes(userId)
                                                        ? formSmsUserIds.filter(id => id !== userId)
                                                        : [...formSmsUserIds, userId];
                                                    setFormSmsUserIds(newSelected);
                                                }
                                            }}
                                            sidealign="left"
                                            topalign="bottom"
                                            toggleDropdown={() => setSmsUsersDropdownOpen(!smsUsersDropdownOpen)}
                                        />
                                    </div> */}
                                </div>
                                <div className="automation-form-footer">
                                    <div className="automation-form-footer-button">
                                        <Button
                                            buttonType="primary-button"
                                            size="large"
                                            text="Save changes"
                                            cta={handleSaveOutput}
                                        disabled={!hasChanges || outputSaving}
                                            spinner={outputSaving}
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                    ) : ( 
                        <div className="automation-detail-section-summary">
                            <div className="automation-detail-output-line">
                                <HugeiconsIcon icon={AiMail02Icon} size={16} color={colour.gray04} />
                                <span className="body-regular colour-secondary">{outputSummary.notifText}</span>
                            </div>
                        </div>
                    )}
        </div>
    );
}

export default OutputSection;
